import formatter from '../utils/formatter';
import { IQueryOptions, IExecutionOptions, IQueryOptionsSort, IQueryContext } from '../models/query';
import { get } from '../utils/get';
import { isNullOrUndefined } from '../utils/typeChecks';
import { ConditionalHelper } from '../helpers/ConditionalHelper';
import { IEntity, IStatistics } from '../models/api/search';
import { IFundManager, IContentItem } from '../models/api/dictionary';
import { Transform } from './transform/Transform';
import { Query } from './Query';
import { TranslationHelper } from '../helpers/TranslationHelper';
import { ContextStore } from '../stores/ContextStore';
import { isArrayLike } from '../utils/isArrayLike';
import { IScenario, IPortfolio, IPortfolioEntity, IScenarioReport } from '../models/portal';
import { IFormat } from '../models/data';
import { deepMergeObjects } from '../utils/object';

export const MAX_CALL_STACK_LEVEL: number = 50;

export abstract class BaseQuery {
	constructor(public options: IQueryOptions, public translationHelper?: TranslationHelper) { }
	abstract getRawValue<T = any>(options: IExecutionOptions, callStackLevel?: number): T;
	execute<T = any>(executionOptions: IExecutionOptions, callStackLevel: number = 1): T {
		if (callStackLevel > MAX_CALL_STACK_LEVEL) {
			console.warn('BaseQuery MAX_CALL_STACK_LEVEL exceeded');
			return;
		}
		const { culture, placeholder: defaultPlaceholder, translate } = executionOptions;
		const { format, placeholder = defaultPlaceholder, transforms, sort } = this.options;
		// Validate that the performance values are allowed and this is a performance value
		if (!this.passesPerformanceCheck(executionOptions)) {
			return placeholder as any;
		}

		let rawValue = this.getRawValue(executionOptions, callStackLevel);
		if (isNullOrUndefined(rawValue)) {
			rawValue = placeholder;
		}

		if (transforms) {
			for (const transformOptions of transforms) {
				const transform = new Transform(transformOptions, this.translationHelper, callStackLevel);
				rawValue = transform.execute(rawValue, this.options, executionOptions);
			}
		}

		if (format) {
			rawValue = this.applyFormat(format, rawValue, executionOptions);
		}

		if (translate && typeof rawValue === 'string') {
			return translate(rawValue) as any as T;
		}
		if (Array.isArray(rawValue) && sort) {
			rawValue = this.applySort(rawValue, sort, culture);
		}
		return rawValue;
	}
	applyFormat(format: IFormat, rawValue: any, executionOptions: IExecutionOptions) {
		const { currency, culture, translate } = executionOptions;
		if (format.globalFormatKey) {
			const globalFormat = executionOptions && executionOptions.globalFormatsByKey && executionOptions.globalFormatsByKey[format.globalFormatKey];
			if (globalFormat && globalFormat.format) {
				format = deepMergeObjects(globalFormat.format, format);
			}
		}
		const { type, transforms } = format;
		const { subQueries = [] } = this.options;
		let workingCurrency = currency;
		if (subQueries) {
			const currencyQueryOptions = subQueries.find(subQuery => subQuery.queryId === 'currency');
			if (currencyQueryOptions) {
				const currencyQuery = new Query(currencyQueryOptions);
				workingCurrency = currencyQuery.execute(executionOptions);
			}
		}
		return formatter(type, culture, workingCurrency, format.options, rawValue, transforms, translate);
	}
	passesPerformanceCheck(executionOptions: IExecutionOptions): boolean {
		const context = Query.getContext(executionOptions, this.options);
		const { isPerformanceAllowed = false } = (context || {});
		const { checkPerformanceAllowed } = this.options;
		return (!checkPerformanceAllowed || isPerformanceAllowed);
	}
	getIndex(targetIndex: number, collection: any[]): number {
		let response = -1;
		if (collection && collection.length > 0 && Math.abs(targetIndex) < collection.length) {
			if (targetIndex >= 0) {
				response = targetIndex;
			}
			else {
				// If the target index is negative take from the back of the array
				response = collection.length + targetIndex;
			}
		}
		return response;
	}

	applySort(collection: any[], sorts: IQueryOptionsSort | IQueryOptionsSort[], culture?: string) {
		if (sorts) {
			const orders = {};
			if (!Array.isArray(sorts) || !isArrayLike(sorts)) {
				sorts = [sorts as IQueryOptionsSort];
			}
			sorts.forEach((sort, sortIndex) => {
				if (sort.order && sort.order.length > 0) {
					const order = sort.translateOrderValues && this.translationHelper && this.translationHelper.translate ?
						sort.order.map(value => this.translationHelper.translate(value)) :
						sort.order;
					const key = `sort_${sortIndex}`;
					orders[key] = order;
					sort['_sortKey'] = key;
				}
			});

			/*
				collection = collection
						.map((value) => {
							const orderIndex = order.indexOf(value[sort.key]);
							value.__order =
								orderIndex > -1 ? orderIndex : order.length;
							return value;
						})
						.sort((a, b) => this.sorters.number(a.__order, b.__order))
						.map((value) => {
							delete value.__order;
							return value;
						});
			*/
			const getOrderValue = (sort: IQueryOptionsSort, value: any) => {
				const order = orders[sort['_sortKey']];
				const orderIndex = order.indexOf(value[sort.key]);
				return orderIndex > -1 ? orderIndex : order.length;
			};
			const innerSort = (a: any, b: any, sort: IQueryOptionsSort) => {
				if (sort.order && sort['_sortKey']) {
					const aOrder = getOrderValue(sort, a);
					const bOrder = getOrderValue(sort, b);
					return this.sorters.number(aOrder, bOrder);
				}
				const sortFunction: (a: any, b: any, culture?: string) => number = this.sorters[sort.type];
				if (sortFunction) {
					const aValue = get(a, sort.key);
					const bValue = get(b, sort.key);
					return sortFunction(aValue, bValue, culture);
				}
				return 0;
			};

			collection.sort((a, b) => {
				for (const sort of (sorts as IQueryOptionsSort[])) {
					let result = innerSort(a, b, sort);
					if (result !== 0) {
						if (sort.direction === 'DESC') {
							result = result * -1;
						}
						return result;
					}
				}
				return 0;
			});
			sorts.forEach((sort) => {
				delete sort['_sortKey'];
			});
		}
		return collection;
	}
	getEntity(executionOptions: IExecutionOptions): IEntity | undefined {
		const conditionalHelper = new ConditionalHelper({ conditions: [] }, this.translationHelper);
		const context = Query.getContext(executionOptions, this.options);
		if (context) {
			const { entityByType } = context;
			const {
				entityType,
				entityConditional,
			} = this.options;
			const entity = entityByType && entityType && entityByType[entityType];
			if (entity && (!entityConditional || conditionalHelper.matches(entity, entityConditional))) {
				return entity as IEntity;
			}
		}
		return;
	}

	getScenarioFromContext(executionOptions: IExecutionOptions): IScenario | undefined {
		const context = Query.getContext(executionOptions, this.options);
		if (context) {
			return context.scenario;
		}
		return;
	}

	getScenarioReportFromContext(executionOptions: IExecutionOptions): IScenarioReport | undefined {
		const context = Query.getContext(executionOptions, this.options);
		if (context) {
			return context.scenarioReport;
		}
		return;
	}

	getPortfolioFromContext(executionOptions: IExecutionOptions): IPortfolio | undefined {
		const context = Query.getContext(executionOptions, this.options);
		if (context) {
			return context.portfolio;
		}
		return;
	}

	getPortfolioEntityFromContext(executionOptions: IExecutionOptions): IPortfolioEntity | undefined {
		const context = Query.getContext(executionOptions, this.options);
		if (context) {
			return context.portfolioEntity;
		}
		return;
	}

	getFundManager(executionOptions: IExecutionOptions): IFundManager | undefined {
		const { fundManagerContext } = executionOptions;
		if (fundManagerContext) {
			return fundManagerContext;
		}
		return;
	}

	getStatistic(executionOptions: IExecutionOptions): IStatistics | undefined {
		const { statisticContext } = executionOptions;
		if (statisticContext) {
			return statisticContext;
		}
		return;
	}

	getSubCollection(executionOptions: IExecutionOptions): any[] {
		const { queryOptionsType } = this.options;
		const entity = this.getEntity(executionOptions);
		if (entity && entity[queryOptionsType]) {
			return entity[queryOptionsType];
		}
		return [];
	}
	getCollectionItem<T = any>(executionOptions: IExecutionOptions): undefined | T {
		const conditionalHelper = new ConditionalHelper({ conditions: [] }, this.translationHelper);
		const {
			code,
			collectionItemConditional = { conditions: [] },
		} = this.options;
		if (code && !collectionItemConditional.conditions.find(condition => condition.field === 'code')) {
			collectionItemConditional.conditions.push({
				field: 'code',
				operator: '=',
				value: code,
			});
		}
		const collectionItem = this.getSubCollection(executionOptions)
			.find(item => conditionalHelper.matches(item, collectionItemConditional));
		return collectionItem;
	}
	getItem(executionOptions: IExecutionOptions, collection: any[]) {
		const { culture } = executionOptions;
		const { sort, startIndex = 0, endIndex, index } = this.options;
		let sortedCollection = this.applySort(collection, sort, culture);
		sortedCollection = sortedCollection.slice(startIndex, endIndex);
		if (sortedCollection && sortedCollection.length > 0) {
			if (typeof index === 'number') {
				const targetIndex = this.getIndex(index, sortedCollection);
				if (targetIndex > -1) {
					const item = sortedCollection[targetIndex];
					return item;
				}
			}
			else {
				return sortedCollection;
			}
		}
		return;
	}
	getRawValueFromSubCollection(executionOptions: IExecutionOptions) {
		const conditionalHelper = new ConditionalHelper({ conditions: [] }, this.translationHelper);
		const {
			key,
			collectionItemValueConditional,
		} = this.options;
		const entity = this.getEntity(executionOptions);
		if (entity) {
			const collectionItem = this.getCollectionItem(executionOptions);
			const { values = [] } = collectionItem || {};
			const matchingValues = values.filter(value => !collectionItemValueConditional || conditionalHelper.matches(value, collectionItemValueConditional));
			if (matchingValues && matchingValues.length > 0) {
				const item = this.getItem(executionOptions, matchingValues);
				if (item) {
					if (key) {
						return item[key];
					}
					return item;
				}
			}
		}
		return;
	}
	getRawValueForContentElement(executionOptions: IExecutionOptions, contentItemKey: string) {
		const { queryOptionsType, key } = this.options;
		if (this.options.entityType) {
			const entity = this.getEntity(executionOptions);
			if (entity) {
				const contentObject = entity[queryOptionsType] as { [key: string]: IContentItem[] };
				if (contentObject) {
					const contentTypeCollection = contentObject[key];
					if (contentTypeCollection) {
						const { culture, fallbackCulture } = executionOptions;
						let contentItem = contentTypeCollection.find(item => item.culture === culture);
						if (!contentItem) {
							contentItem = contentTypeCollection.find(item => item.culture === fallbackCulture);
						}
						return (contentItem && contentItem[contentItemKey]) as any;
					}
				}
			}
		}
		else {
			const keysByContentItemKey = {
				'commentary': {
					globalContextKey: 'globalCommentaries',
					contentParentKey: 'commentaryType',
					contentValuesKey: 'commentaries',
				},
				'disclaimer': {
					globalContextKey: 'globalDisclaimers',
					contentParentKey: 'disclaimerType',
					contentValuesKey: 'disclaimers',
				},
			};
			const keysForContentItemKey = keysByContentItemKey[contentItemKey];
			if (keysForContentItemKey) {
				const { globalContextKey, contentParentKey, contentValuesKey } = keysForContentItemKey;
				const context = Query.getContext(executionOptions, this.options);
				if (context) {
					const collection = context[globalContextKey] as any[];
					if (collection && Array.isArray(collection)) {
						const contentParent = collection.find(item => item[contentParentKey] === key);
						if (contentParent) {
							const contentTypeCollection = contentParent[contentValuesKey];
							if (contentTypeCollection) {
								const { culture, fallbackCulture } = executionOptions;
								let contentItem = contentTypeCollection.find(item => item.culture === culture);
								if (!contentItem) {
									contentItem = contentTypeCollection.find(item => item.culture === fallbackCulture);
								}
								return (contentItem && contentItem[contentItemKey]) as any;
							}
						}
					}

				}
			}
		}
	}
	sorters = {
		date: (a: string | number, b: string | number) => {
			const aDate = new Date(a);
			const bDate = new Date(b);
			return Number(aDate) - Number(bDate);
		},
		number: (a: number, b: number) => {
			return a - b;
		},
		string: (a: string, b: string, culture) => {
			const aString = a.toString().toUpperCase();
			const bString = b.toString().toUpperCase();
			return aString.localeCompare(bString, culture);
		},
	};
}